import { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Box,
  Input,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2, LuRotateCcw } from 'react-icons/lu';
import { HexColorPicker } from 'react-colorful';
import { toast } from 'react-toastify';
import ColorPickerInput from '../ColorPickerInput';
import MenuContent from '../MenuContent';
import {
  updateMenu,
  replaceBadgeDefinitions,
  translateFields,
  type MenuResponse,
  type MenuTheme,
  type BadgeDefinition,
  type TranslatableField,
} from '../../api/menus';
import ModalShell, { CancelButton, SaveButton, LanguageTabs } from './ModalShell';
import { LANGUAGE_OPTIONS } from '../../constants/currencies';
import type { MenuContentCategory } from '../MenuContent';

const THEME_PRESETS: { id: string; label: string; theme: MenuTheme }[] = [
  {
    id: 'light',
    label: 'Light',
    theme: {
      background_color: '#FFFFFF',
      text_color: '#111111',
      secondary_text_color: '#666666',
      accent_color: '#111111',
      border_color: '#EEEEEE',
      price_color: '#111111',
    },
  },
  {
    id: 'dark',
    label: 'Dark',
    theme: {
      background_color: '#1A1A1A',
      text_color: '#F5F5F5',
      secondary_text_color: '#999999',
      accent_color: '#F5F5F5',
      border_color: '#333333',
      price_color: '#F5F5F5',
    },
  },
  {
    id: 'black',
    label: 'Black',
    theme: {
      background_color: '#000000',
      text_color: '#E5E5E5',
      secondary_text_color: '#777777',
      accent_color: '#E5E5E5',
      border_color: '#1A1A1A',
      price_color: '#E5E5E5',
    },
  },
  {
    id: 'midnight',
    label: 'Midnight',
    theme: {
      background_color: '#0F172A',
      text_color: '#E2E8F0',
      secondary_text_color: '#94A3B8',
      accent_color: '#38BDF8',
      border_color: '#1E293B',
      price_color: '#38BDF8',
    },
  },
];

const getActivePresetId = (theme: MenuTheme): string | null => {
  for (const preset of THEME_PRESETS) {
    const match = Object.keys(preset.theme).every(
      (key) => preset.theme[key as keyof MenuTheme] === theme[key as keyof MenuTheme]
    );
    if (match) return preset.id;
  }
  return null;
};

interface ThemeModalProps {
  open: boolean;
  onClose: () => void;
  menu: MenuResponse;
  menuTheme: MenuTheme;
  badgeDefinitions: BadgeDefinition[];
  menuContentCategories: MenuContentCategory[];
  availableLanguages: string[];
  defaultLanguage: string;
  viewLang: string;
  getTranslated: (field: TranslatableField | null | undefined) => string;
  onMenuUpdate: (menu: MenuResponse) => void;
  onThemeUpdate: (theme: MenuTheme) => void;
  onBadgesUpdate: (badges: BadgeDefinition[]) => void;
  draftTheme: MenuTheme;
  onDraftThemeChange: (theme: MenuTheme) => void;
  draftBadges: BadgeDefinition[];
  onDraftBadgesChange: (badges: BadgeDefinition[]) => void;
}

const ThemeModal = ({
  open,
  onClose,
  menu,
  menuTheme,
  badgeDefinitions,
  menuContentCategories,
  availableLanguages,
  defaultLanguage,
  viewLang,
  getTranslated,
  onMenuUpdate,
  onThemeUpdate,
  onBadgesUpdate,
  draftTheme,
  onDraftThemeChange,
  draftBadges,
  onDraftBadgesChange,
}: ThemeModalProps) => {
  const { t } = useTranslation();
  const [themeMode, setThemeMode] = useState<string>('custom');
  const [badgeLang, setBadgeLang] = useState(defaultLanguage);
  const [newBadgeLabel, setNewBadgeLabel] = useState('');
  const [newBadgeKey, setNewBadgeKey] = useState('');
  const [newBadgeColor, setNewBadgeColor] = useState('#4CAF50');
  const [previewPage, setPreviewPage] = useState<1 | 2>(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslatingBadges, setIsTranslatingBadges] = useState(false);

  useEffect(() => {
    if (open) {
      setThemeMode(getActivePresetId(menuTheme) || 'custom');
      setBadgeLang(defaultLanguage);
      setNewBadgeLabel('');
      setNewBadgeKey('');
      setNewBadgeColor('#4CAF50');
    }
  }, [open]);

  const hasChanges = (() => {
    const themeChanged = Object.keys(draftTheme).some(
      (key) => draftTheme[key as keyof MenuTheme] !== menuTheme[key as keyof MenuTheme]
    );
    const badgesChanged =
      draftBadges.length !== badgeDefinitions.length ||
      draftBadges.some((b, i) => {
        const orig = badgeDefinitions[i];
        return !orig || b.key !== orig.key || b.color !== orig.color ||
          JSON.stringify(b.label) !== JSON.stringify(orig.label);
      });
    return themeChanged || badgesChanged;
  })();

  const handleReset = () => {
    onDraftThemeChange({ ...menuTheme });
    setThemeMode(getActivePresetId(menuTheme) || 'custom');
    onDraftBadgesChange(badgeDefinitions.map(b => ({ ...b })));
    setNewBadgeLabel('');
    setNewBadgeKey('');
    setNewBadgeColor('#4CAF50');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await updateMenu(menu.id, { theme: draftTheme });
      onMenuUpdate(updated);
      onThemeUpdate({ ...draftTheme });
      const savedBadges = await replaceBadgeDefinitions(menu.id, draftBadges);
      onBadgesUpdate(savedBadges);
      toast.success(t('menus.menuUpdated'));
      onClose();
    } catch (error) {
      toast.error(t('menus.updateFailed'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const addNewBadge = () => {
    if (newBadgeLabel.trim() && newBadgeKey && !draftBadges.some(b => b.key === newBadgeKey)) {
      onDraftBadgesChange([...draftBadges, { key: newBadgeKey, label: { [defaultLanguage]: newBadgeLabel.trim() }, color: newBadgeColor }]);
      setNewBadgeLabel('');
      setNewBadgeKey('');
      setNewBadgeColor('#4CAF50');
    }
  };

  const canAddBadge = newBadgeLabel.trim() && newBadgeKey && !draftBadges.some(b => b.key === newBadgeKey);

  const handleAutoTranslateBadges = async () => {
    const fields: { [key: string]: string } = {};
    const badgeIndices: number[] = [];
    draftBadges.forEach((badge, i) => {
      const defaultLabel = badge.label[defaultLanguage]?.trim();
      if (defaultLabel) {
        fields[`badge_${i}`] = defaultLabel;
        badgeIndices.push(i);
      }
    });

    if (badgeIndices.length === 0) {
      toast.error(t('menus.fillDefaultLanguageFirst', 'Please fill in the default language fields first.'));
      return;
    }

    const targetLanguages = availableLanguages.filter(lang => {
      if (lang === defaultLanguage) return false;
      return badgeIndices.some(i => !draftBadges[i].label[lang]?.trim());
    });

    if (targetLanguages.length === 0) {
      toast.info(t('menus.allFieldsFilled', 'All translation fields are already filled.'));
      return;
    }

    setIsTranslatingBadges(true);
    try {
      const result = await translateFields(menu.id, { fields, target_languages: targetLanguages });
      const updated = draftBadges.map((badge, i) => {
        if (!badgeIndices.includes(i)) return badge;
        const newLabel = { ...badge.label };
        for (const lang of targetLanguages) {
          const key = `badge_${i}`;
          if (!newLabel[lang]?.trim() && result.translations[lang]?.[key]) {
            newLabel[lang] = result.translations[lang][key];
          }
        }
        return { ...badge, label: newLabel };
      });
      onDraftBadgesChange(updated);
      toast.success(t('menus.translationComplete', 'Translations filled. Review and save.'));
    } catch (error) {
      toast.error(t('menus.translationFailed', 'Auto-translation failed.'));
      console.error(error);
    } finally {
      setIsTranslatingBadges(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={t('menus.themeSettings')}
      maxW="560px"
      footer={
        <>
          {hasChanges && (
            <Box
              as="button"
              mr="auto"
              display="flex"
              alignItems="center"
              gap="5px"
              px="12px"
              py="7px"
              fontSize="13px"
              fontWeight="400"
              color="neutral.400"
              backgroundColor="transparent"
              borderWidth="1px"
              borderColor="neutral.700"
              borderRadius="6px"
              cursor="pointer"
              _hover={{ borderColor: 'neutral.500', color: 'neutral.300' }}
              onClick={handleReset}
            >
              <LuRotateCcw size={13} />
              Reset
            </Box>
          )}
          <CancelButton onClick={onClose} label={t('common.cancel')} />
          <SaveButton onClick={handleSave} loading={isSaving} label={t('common.save')} />
        </>
      }
    >
              <Flex direction="column" rowGap="20px">
                {/* Preset buttons */}
                <Flex gap="8px" flexWrap="wrap">
                  {THEME_PRESETS.map((preset) => {
                    const isActive = themeMode !== 'custom' && getActivePresetId(draftTheme) === preset.id;
                    return (
                      <Flex
                        key={preset.id}
                        alignItems="center"
                        gap="6px"
                        px="12px"
                        py="7px"
                        fontSize="13px"
                        fontWeight="400"
                        color={isActive ? 'neutral.900' : 'neutral.400'}
                        backgroundColor={isActive ? 'neutral.100' : 'transparent'}
                        borderWidth="1px"
                        borderColor={isActive ? 'neutral.100' : 'neutral.700'}
                        borderRadius="6px"
                        cursor="pointer"
                        _hover={{ borderColor: 'neutral.500' }}
                        onClick={() => {
                          onDraftThemeChange({ ...preset.theme });
                          setThemeMode(preset.id);
                        }}
                      >
                        <Box
                          width="14px"
                          height="14px"
                          borderRadius="full"
                          backgroundColor={preset.theme.background_color}
                          borderWidth="1px"
                          borderColor="neutral.600"
                          flexShrink={0}
                        />
                        {preset.label}
                      </Flex>
                    );
                  })}
                  <Box
                    px="12px"
                    py="7px"
                    fontSize="13px"
                    fontWeight="400"
                    color={themeMode === 'custom' ? 'neutral.900' : 'neutral.400'}
                    backgroundColor={themeMode === 'custom' ? 'neutral.100' : 'transparent'}
                    borderWidth="1px"
                    borderColor={themeMode === 'custom' ? 'neutral.100' : 'neutral.700'}
                    borderRadius="6px"
                    cursor="pointer"
                    _hover={{ borderColor: 'neutral.500' }}
                    onClick={() => setThemeMode('custom')}
                  >
                    Custom
                  </Box>
                </Flex>

                {/* Custom color fields */}
                {themeMode === 'custom' && (
                  <Box
                    backgroundColor="neutral.700"
                    borderRadius="8px"
                    p="16px"
                  >
                    <Flex direction="column" rowGap="16px">
                      <ColorGroup
                        label={t('menus.textColor')}
                        fields={[
                          { key: 'text_color', label: t('menus.textColor') },
                          { key: 'secondary_text_color', label: t('menus.secondaryTextColor') },
                          { key: 'price_color', label: t('menus.priceColor') },
                        ]}
                        theme={draftTheme}
                        onChange={(key, color) => onDraftThemeChange({ ...draftTheme, [key]: color })}
                      />
                      <ColorGroup
                        label="Background & Accents"
                        fields={[
                          { key: 'background_color', label: t('menus.backgroundColor') },
                          { key: 'accent_color', label: t('menus.accentColor') },
                          { key: 'border_color', label: t('menus.borderColor') },
                        ]}
                        theme={draftTheme}
                        onChange={(key, color) => onDraftThemeChange({ ...draftTheme, [key]: color })}
                      />
                    </Flex>
                  </Box>
                )}

                {/* Badge Definitions */}
                <Box>
                  <Flex alignItems="center" justifyContent="space-between" mb="10px">
                    <SectionLabel>Badges</SectionLabel>
                  </Flex>
                  <LanguageTabs
                    languages={availableLanguages}
                    activeLang={badgeLang}
                    onSelect={setBadgeLang}
                    options={LANGUAGE_OPTIONS}
                    onAutoTranslate={availableLanguages.length > 1 && draftBadges.length > 0 ? handleAutoTranslateBadges : undefined}
                    isTranslating={isTranslatingBadges}
                  />
                  <Flex direction="column" gap="8px" mt="10px">
                    {draftBadges.map((badge, index) => (
                      <Flex key={badge.key} alignItems="center" gap="8px">
                        <ColorSwatch
                          color={badge.color}
                          onChange={(color) => {
                            const updated = [...draftBadges];
                            updated[index] = { ...updated[index], color };
                            onDraftBadgesChange(updated);
                          }}
                        />
                        <Input
                          value={badge.label[badgeLang] || ''}
                          onChange={(e) => {
                            const updated = [...draftBadges];
                            updated[index] = { ...updated[index], label: { ...updated[index].label, [badgeLang]: e.target.value } };
                            onDraftBadgesChange(updated);
                          }}
                          placeholder={badge.label[defaultLanguage] || badge.key}
                          p="6px 10px"
                          fontWeight="400"
                          fontSize="13px"
                          color="neutral.300"
                          borderWidth="1px"
                          borderColor="neutral.700"
                          backgroundColor="neutral.700"
                          borderRadius="6px"
                          flex="1"
                          _focus={{ borderColor: 'neutral.600', outline: 'none' }}
                        />
                        <Box
                          cursor="pointer"
                          color="neutral.500"
                          _hover={{ color: 'error.600' }}
                          onClick={() => onDraftBadgesChange(draftBadges.filter((_, i) => i !== index))}
                          flexShrink={0}
                        >
                          <LuTrash2 size={14} />
                        </Box>
                      </Flex>
                    ))}

                    {/* Add new badge */}
                    <Flex alignItems="center" gap="8px" mt="4px">
                      <ColorSwatch color={newBadgeColor} onChange={setNewBadgeColor} />
                      <Input
                        value={newBadgeLabel}
                        onChange={(e) => {
                          setNewBadgeLabel(e.target.value);
                          setNewBadgeKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
                        }}
                        placeholder="New badge name..."
                        p="6px 10px"
                        fontWeight="400"
                        fontSize="13px"
                        color="neutral.300"
                        borderWidth="1px"
                        borderColor="neutral.700"
                        backgroundColor="neutral.700"
                        borderRadius="6px"
                        flex="1"
                        _focus={{ borderColor: 'neutral.600', outline: 'none' }}
                        onKeyDown={(e) => { if (e.key === 'Enter') addNewBadge(); }}
                      />
                      <Box
                        cursor={canAddBadge ? 'pointer' : 'not-allowed'}
                        color={canAddBadge ? 'neutral.300' : 'neutral.600'}
                        _hover={canAddBadge ? { color: 'neutral.100' } : undefined}
                        onClick={addNewBadge}
                        flexShrink={0}
                      >
                        <LuPlus size={16} />
                      </Box>
                    </Flex>
                  </Flex>
                </Box>

                {/* Live preview */}
                <Box>
                  <Flex alignItems="center" justifyContent="space-between" mb="10px">
                    <SectionLabel>Preview</SectionLabel>
                    {menuContentCategories.length > 1 && (
                      <PageToggle page={previewPage} onChange={setPreviewPage} t={t} />
                    )}
                  </Flex>
                  <Box
                    borderRadius="8px"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="neutral.700"
                    height={{ base: '280px', md: '350px' }}
                    position="relative"
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      width="200%"
                      height="200%"
                      css={{ transform: 'scale(0.5)', transformOrigin: 'top left' }}
                    >
                      <MenuContent
                        name={getTranslated(menu?.name) || ''}
                        description={getTranslated(menu?.description) || null}
                        currency={menu?.currency || 'USD'}
                        categories={menuContentCategories}
                        theme={draftTheme}
                        storeInfo={menu?.store_info || undefined}
                        badgeDefinitions={draftBadges.map(b => ({ key: b.key, label: getTranslated(b.label), color: b.color }))}
                        availableLanguages={[defaultLanguage, ...availableLanguages.filter(l => l !== defaultLanguage)]}
                        currentLanguage={viewLang}
                        page={previewPage}
                        onPageChange={setPreviewPage}
                      />
                    </Box>
                  </Box>
                </Box>
              </Flex>
    </ModalShell>
  );
};

// Small helpers used only in this file

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Box fontSize="12px" fontWeight="500" color="neutral.500" textTransform="uppercase" letterSpacing="0.05em">
    {children}
  </Box>
);

const ColorSwatch = ({ color, onChange }: { color: string; onChange: (c: string) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <Box ref={ref} position="relative" flexShrink={0}>
      <Box
        width="24px"
        height="24px"
        borderRadius="4px"
        backgroundColor={color}
        cursor="pointer"
        borderWidth="1px"
        borderColor="neutral.600"
        _hover={{ borderColor: 'neutral.500' }}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && (
        <Box
          position="absolute"
          top="30px"
          left="0"
          zIndex={20}
          backgroundColor="neutral.800"
          borderRadius="12px"
          borderWidth="1px"
          borderColor="neutral.700"
          p="12px"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
        >
          <HexColorPicker color={color.startsWith('#') ? color : `#${color}`} onChange={onChange} />
          <Input
            value={color}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            mt="10px"
            p="8px"
            fontWeight="400"
            fontSize="13px"
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.700"
            borderRadius="6px"
            _focus={{ borderColor: 'neutral.600', outline: 'none' }}
          />
        </Box>
      )}
    </Box>
  );
};

const ColorGroup = ({
  label,
  fields,
  theme,
  onChange,
}: {
  label: string;
  fields: { key: string; label: string }[];
  theme: MenuTheme;
  onChange: (key: string, color: string) => void;
}) => (
  <Box>
    <Box fontSize="12px" fontWeight="500" color="neutral.500" textTransform="uppercase" letterSpacing="0.05em" mb="10px">
      {label}
    </Box>
    <Flex gap="10px" flexWrap="wrap">
      {fields.map(({ key, label }) => (
        <Flex key={key} alignItems="center" gap="8px">
          <ColorPickerInput
            value={theme[key as keyof MenuTheme]}
            onChange={(color) => onChange(key, color)}
            size={28}
          />
          <Box fontWeight="400" fontSize="13px" color="neutral.400" whiteSpace="nowrap">{label}</Box>
        </Flex>
      ))}
    </Flex>
  </Box>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PageToggle = ({ page, onChange, t }: { page: 1 | 2; onChange: (p: 1 | 2) => void; t: any }) => (
  <Flex backgroundColor="neutral.700" borderRadius="6px" p="2px" gap="2px">
    {([
      { p: 1 as const, label: t('menus.categories', 'Categories') },
      { p: 2 as const, label: t('menus.items', 'Items') },
    ]).map(({ p, label }) => (
      <Box
        key={p}
        px="10px"
        py="3px"
        borderRadius="4px"
        fontSize="11px"
        fontWeight="500"
        cursor="pointer"
        color={page === p ? 'neutral.100' : 'neutral.500'}
        backgroundColor={page === p ? 'neutral.600' : 'transparent'}
        onClick={() => onChange(p)}
      >
        {label}
      </Box>
    ))}
  </Flex>
);

export default ThemeModal;
